import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, DestroyRef, inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { firstValueFrom, lastValueFrom } from 'rxjs';

import { Client } from 'src/providers/client/client';

import { ConciergeApiService } from 'src/shared/services/concierge-api.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { ComplianceApiService } from 'src/shared/services/compliance-api.service';

import { ComplianceItem, ComplianceBucket, ComplianceCheckStatus } from 'src/models/compliance.model';

interface BucketGroup {
  bucket: ComplianceBucket;
  label: string;
  icon: string;
  items: ComplianceItem[];
  expanded: boolean;
}

@Component({
  selector: 'legal-compliance-onboarding',
  templateUrl: './legal-compliance-onboarding.component.html',
  styleUrls: ['./legal-compliance-onboarding.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class LegalComplianceOnboardingComponent implements OnInit, OnDestroy {
  private client = inject(Client);
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private util = inject(Util);
  private storage = inject(TidyStorage);
  private complianceApi = inject(ComplianceApiService);
  private conciergeApi = inject(ConciergeApiService);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  windowService = inject(WindowService);

  loaded = false;
  fromGettingStarted = false;
  submitted = false;
  private metadataKey: string;
  private readonly stepId = 'legal';
  private addressId: number;

  currentStep = 1;

  complianceCheckStatus: ComplianceCheckStatus = null;
  complianceItems: ComplianceItem[] = [];
  bucketGroups: BucketGroup[] = [];
  isRunningCheck = false;
  expandedItemIds = new Set<number>();
  selectedItemIds = new Set<number>();
  allSuggestionsSelected = true;
  private pollingInterval: any = null;
  private readonly POLLING_INTERVAL_MS = 12000;

  itemForm = this.fb.group({
    method: [''],
    body: ['']
  });

  private readonly BUCKET_CONFIG: { bucket: ComplianceBucket; label: string; icon: string }[] = [
    { bucket: 'blockers', label: 'Blockers', icon: 'assets/svg/alert-circle-outline.svg' },
    { bucket: 'before_listing', label: 'Before Listing', icon: 'assets/svg/clipboard-outline.svg' },
    { bucket: 'operate', label: 'Operate', icon: 'assets/svg/shield-checkmark-outline.svg' },
    { bucket: 'taxes', label: 'Taxes', icon: 'assets/svg/cash-outline.svg' },
    { bucket: 'admin', label: 'Admin', icon: 'assets/svg/document-text-outline.svg' },
    { bucket: 'recommended', label: 'Recommended', icon: 'assets/svg/star-outline.svg' },
  ];

  get totalSteps(): number {
    return this.itemForm.value.method === 'nothing' ? 2 : 3;
  }

  get progressPercent(): number {
    return Math.round((this.currentStep / this.totalSteps) * 100);
  }

  constructor() {
    this.destroyRef.onDestroy(() => this.stopPolling());
  }

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    this.addressId = params?.addressId;
    this.fromGettingStarted = !!params?.fromGettingStarted;
    this.metadataKey = `onboarding_step.${this.addressId}.${this.stepId}`;
    this.rightSidePanelService.setDialogPageTitle('Legal / Compliance');
    await this.loadExistingItems();

    const runningKey = `compliance_report_running_${this.addressId}`;
    if (localStorage.getItem(runningKey) === 'true') {
      this.itemForm.controls.method.setValue('track');
      this.currentStep = 2;
      this.updateBackAction();
      this.isRunningCheck = true;
      this.complianceCheckStatus = 'pending';
      this.startPolling();
    }

    this.loaded = true;
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.rightSidePanelService.customBackAction$.next(null);
  }

  private updateBackAction() {
    if (this.currentStep > 1) {
      this.rightSidePanelService.customBackAction$.next(() => {
        this.currentStep = this.currentStep - 1;
        this.cdr.markForCheck();
        this.updateBackAction();
      });
    } else {
      this.rightSidePanelService.customBackAction$.next(null);
    }
  }

  selectMethod = async (method: 'nothing' | 'track') => {
    this.itemForm.controls.method.setValue(method);
    this.currentStep = 2;
    this.updateBackAction();
    this.cdr.markForCheck();
    if (method === 'track' && this.complianceItems.length === 0) {
      await this.runComplianceReport();
    }
  };

  goToNextStep = async () => {
    this.currentStep = this.currentStep + 1;
    this.updateBackAction();
    this.cdr.markForCheck();
  };

  finish = async () => {
    try {
      const noteBody = this.itemForm.value.body?.trim();
      if (noteBody) {
        const data = {
          body: noteBody,
          subject: 'Legal / Compliance notes',
          attachments: [] as string[],
          emails: [] as string[]
        };
        await lastValueFrom(this.conciergeApi.createConciergeItem({ data, type: 'support.concierge_task' }));
      }
      await this.markStepCompleted();
      this.navigateBack();
    } catch (err) {
      this.util.showError((err as any)?.error?.message || (err as Error).message, 10000);
    }
  };

  private async markStepCompleted() {
    await this.client.addMetadata({
      key: this.metadataKey,
      value: 'completed'
    });
  }

  private navigateBack() {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateForward('getting-started');
    }
  }

  private async loadExistingItems() {
    if (!this.addressId) { return; }
    try {
      // Prefer the new report endpoint; fall back to the legacy item list
      // for properties that still pre-date the displayPlan rollout.
      const report = await firstValueFrom(this.complianceApi.getReport(this.addressId));
      if (report?.sections?.length) {
        this.complianceItems = report.sections.flatMap(s => s.items || []);
      } else {
        this.complianceItems = await firstValueFrom(this.complianceApi.getItems(this.addressId));
      }
      this.bucketGroups = this.groupByBucket(this.complianceItems);
      this.selectedItemIds.clear();
      this.complianceItems.forEach(item => this.selectedItemIds.add(item.id));
      this.allSuggestionsSelected = this.complianceItems.length > 0;
    } catch {
      this.complianceItems = [];
      this.bucketGroups = [];
    }
  }

  private groupByBucket(items: ComplianceItem[]): BucketGroup[] {
    const groups = this.BUCKET_CONFIG
      .map(b => ({
        ...b,
        items: items.filter(i => i.bucket === b.bucket),
        expanded: false,
      }))
      .filter(g => g.items.length > 0);
    if (groups.length) {
      groups[0].expanded = true;
    }
    return groups;
  }

  toggleBucket(group: BucketGroup) {
    group.expanded = !group.expanded;
    this.cdr.markForCheck();
  }

  toggleItem(itemId: number) {
    if (this.expandedItemIds.has(itemId)) {
      this.expandedItemIds.delete(itemId);
    } else {
      this.expandedItemIds.add(itemId);
    }
    this.cdr.markForCheck();
  }

  isItemExpanded(itemId: number): boolean {
    return this.expandedItemIds.has(itemId);
  }

  // Only worth expanding when there is detail beyond the title already shown.
  hasItemDetail(item: ComplianceItem): boolean {
    return !!item.how_to_do_it || !!(item.name && item.what_to_do);
  }

  toggleItemSelected(itemId: number) {
    if (this.selectedItemIds.has(itemId)) {
      this.selectedItemIds.delete(itemId);
    } else {
      this.selectedItemIds.add(itemId);
    }
    this.allSuggestionsSelected = this.complianceItems.length > 0 &&
      this.complianceItems.every(item => this.selectedItemIds.has(item.id));
    this.cdr.markForCheck();
  }

  toggleAllSuggestions() {
    this.allSuggestionsSelected = !this.allSuggestionsSelected;
    if (this.allSuggestionsSelected) {
      this.complianceItems.forEach(item => this.selectedItemIds.add(item.id));
    } else {
      this.selectedItemIds.clear();
    }
    this.cdr.markForCheck();
  }

  isItemSelected(itemId: number): boolean {
    return this.selectedItemIds.has(itemId);
  }

  runComplianceReport = async () => {
    if (!this.addressId) { return; }
    try {
      this.isRunningCheck = true;
      this.complianceCheckStatus = 'pending';
      localStorage.setItem(`compliance_report_running_${this.addressId}`, 'true');
      this.cdr.markForCheck();
      await firstValueFrom(this.complianceApi.triggerCheck(this.addressId));
      this.startPolling();
    } catch (err) {
      this.isRunningCheck = false;
      this.complianceCheckStatus = 'failed';
      localStorage.removeItem(`compliance_report_running_${this.addressId}`);
      this.cdr.markForCheck();
      this.util.showError('Failed to start compliance check. Please try again.');
    }
  };

  private startPolling() {
    this.stopPolling();
    this.pollingInterval = setInterval(async () => {
      await this.pollCheckStatus();
    }, this.POLLING_INTERVAL_MS);
  }

  private stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private async pollCheckStatus() {
    if (!this.addressId) {
      this.stopPolling();
      return;
    }
    try {
      const check = await firstValueFrom(this.complianceApi.getCheckStatus(this.addressId));
      const status = check?.status || null;

      if (status === 'completed') {
        this.stopPolling();
        this.isRunningCheck = false;
        this.complianceCheckStatus = 'completed';
        localStorage.removeItem(`compliance_report_running_${this.addressId}`);
        await this.loadExistingItems();
        this.cdr.markForCheck();
      } else if (status === 'failed') {
        this.stopPolling();
        this.isRunningCheck = false;
        this.complianceCheckStatus = 'failed';
        localStorage.removeItem(`compliance_report_running_${this.addressId}`);
        this.cdr.markForCheck();
      }
    } catch {
      // Silently retry on next interval
    }
  }

  getSelectedItems(): ComplianceItem[] {
    return this.complianceItems.filter(item => this.selectedItemIds.has(item.id));
  }
}
